import { useEffect, useMemo, useState, type ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { materialTransfersByRequestUrl } from '../../constants';
import type { ProcurementRequest } from '../../domain/types';

export type LinkedMaterialTransfer = {
  id: string;
  transferNumber: string;
  status: string;
};

function formatTransferStatus(status: string): string {
  const s = (status ?? '').trim();
  if (!s) {
    return '';
  }
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function fallbackLinks(request: ProcurementRequest): LinkedMaterialTransfer[] {
  const rows: LinkedMaterialTransfer[] = [];
  const fulfilledId = (request.fulfilledByMaterialTransferId ?? '').trim();
  const fulfilledNumber = (request.fulfilledByMaterialTransferNumber ?? '').trim();
  if (fulfilledId) {
    rows.push({
      id: fulfilledId,
      transferNumber: fulfilledNumber || 'Material transfer',
      status: ''
    });
  }
  const sourcingId = (request.sourcingInitiatedByMaterialTransferId ?? '').trim();
  if (sourcingId && sourcingId.toLowerCase() !== fulfilledId.toLowerCase()) {
    rows.push({
      id: sourcingId,
      transferNumber: 'Open transfer',
      status: ''
    });
  }
  return rows;
}

export function RequestLinkedMaterialTransfers(props: {
  request: ProcurementRequest;
  authFetch: (url: string, init?: RequestInit) => Promise<Response>;
}): ReactElement | null {
  const { request, authFetch } = props;
  const seeded = useMemo(() => fallbackLinks(request), [request]);
  const [rows, setRows] = useState<LinkedMaterialTransfer[]>(seeded);

  useEffect(() => {
    setRows(seeded);
    const requestId = (request.id ?? '').trim();
    if (!requestId) {
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const res = await authFetch(materialTransfersByRequestUrl(requestId));
        if (!res.ok) {
          return;
        }
        const list = (await res.json()) as LinkedMaterialTransfer[];
        if (cancelled || !Array.isArray(list) || list.length === 0) {
          return;
        }
        setRows(
          list
            .filter((t) => (t.id ?? '').trim())
            .map((t) => ({
              id: t.id.trim(),
              transferNumber: (t.transferNumber ?? '').trim() || t.id.trim(),
              status: (t.status ?? '').trim()
            }))
        );
      } catch {
        /* keep seeded links */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authFetch, request.id, seeded]);

  if (rows.length === 0) {
    return null;
  }

  return (
    <>
      <dt>Material transfer</dt>
      <dd>
        <ul className="request-linked-transfers">
          {rows.map((t) => {
            const st = formatTransferStatus(t.status);
            return (
              <li key={t.id}>
                <Link to={`/material-transfers/${encodeURIComponent(t.id)}/edit`}>{t.transferNumber}</Link>
                {st ? <span className="hint"> ({st})</span> : null}
              </li>
            );
          })}
        </ul>
      </dd>
    </>
  );
}
